import {
  axiosConfig,
  del,
  patch,
  type RequestConfig,
} from "../shared/api-client";
import type {
  ApiItemResponse,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";
import type {
  AdmissionInquiry,
  AdmissionInquiryPayload,
  AdmissionInquiryRecord,
  AdmissionInquiryStats,
} from "@project/types/admission-inquiry";
import { JIVANDHARA_BASE, fetchListPage, unwrapItem } from "./list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/admission-inquiries`;
const SILENT: RequestConfig = { showNotification: false };

class AdmissionInquiryService {
  listInquiriesPage = (
    query: ListQuery = {}
  ): Promise<
    PaginatedResult<AdmissionInquiryRecord, AdmissionInquiryStats>
  > => {
    return fetchListPage<AdmissionInquiryRecord, AdmissionInquiryStats>(
      `${BASE}/`,
      query
    );
  };

  getInquiry = async (
    id: number
  ): Promise<AdmissionInquiryRecord | undefined> => {
    const response = await axiosConfig.get<
      ApiItemResponse<AdmissionInquiryRecord> | AdmissionInquiryRecord
    >(`${BASE}/${id}/`, SILENT);
    return unwrapItem(response.data);
  };

  create = async (
    payload: AdmissionInquiryPayload
  ): Promise<AdmissionInquiry> => {
    const response = await axiosConfig.post<
      ApiItemResponse<AdmissionInquiry> | AdmissionInquiry
    >(`${BASE}/`, payload, SILENT);
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to submit inquiry.");
    }
    return item;
  };

  update = async (
    id: number,
    payload: AdmissionInquiryPayload
  ): Promise<AdmissionInquiryRecord | undefined> => {
    const data = await patch<
      ApiItemResponse<AdmissionInquiryRecord> | AdmissionInquiryRecord,
      AdmissionInquiryPayload
    >(`${BASE}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };

  remove = async (id: number): Promise<void> => {
    await del(`${BASE}/${id}/`, SILENT);
  };

  approve = async (id: number): Promise<AdmissionInquiryRecord> => {
    const response = await axiosConfig.post<
      ApiItemResponse<AdmissionInquiryRecord> | AdmissionInquiryRecord
    >(`${BASE}/${id}/approve/`, {}, SILENT);
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to approve inquiry.");
    }
    return item;
  };

  reject = async (id: number): Promise<AdmissionInquiryRecord> => {
    const response = await axiosConfig.post<
      ApiItemResponse<AdmissionInquiryRecord> | AdmissionInquiryRecord
    >(`${BASE}/${id}/reject/`, {}, SILENT);
    const item = unwrapItem(response.data);
    if (!item) {
      throw new Error("Failed to reject inquiry.");
    }
    return item;
  };
}

export const admissionInquiryService = new AdmissionInquiryService();
